import { Ghost, LogOut, X } from 'lucide-react';
import { NAV } from '@/lib/navigation';
import { NavGroup } from './NavGroup';
import { useSession, logout } from '@/lib/auth';

function initials(email?: string): string {
  if (!email) return '·';
  const name = email.split('@')[0] || '';
  const parts = name.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '·';
}

export function Sidebar() {
  const { user } = useSession();
  return (
    <aside className="hidden h-full w-[224px] shrink-0 flex-col border-r border-black/[0.06] bg-sidebar sm:flex">
      {/* Brand */}
      <div className="flex items-center gap-2.5 px-4 pb-3 pt-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-b from-[#3a9bff] to-primary shadow-glow">
          <Ghost className="h-[16px] w-[16px] text-white" strokeWidth={2.2} aria-hidden />
        </div>
        <div className="leading-none">
          <div className="text-[13px] font-semibold tracking-tight">GhostFit</div>
          <div className="mt-[3px] text-[9px] font-semibold uppercase tracking-[0.12em] text-subtle">Console</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2.5 pb-3">
        {NAV.map((g) => (
          <NavGroup key={g.label} group={g} />
        ))}
      </nav>

      {/* User */}
      <div className="flex items-center gap-2 border-t border-black/[0.06] px-3 py-2.5">
        <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-[#5a5a5f] to-[#3a3a3f] text-[9.5px] font-semibold text-white">
          {initials(user?.email)}
        </div>
        <div className="min-w-0 flex-1 truncate text-[11px] text-muted" title={user?.email}>
          {user?.email || '—'}
        </div>
        <button
          onClick={logout}
          title="Sign out"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-subtle transition-colors hover:bg-black/[0.05] hover:text-text"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </aside>
  );
}

export function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useSession();
  if (!open) return null;
  return (
    <>
      <button type="button" aria-label="Close navigation" className="fixed inset-0 z-40 bg-black/25 sm:hidden" onClick={onClose} />
      <aside id="mobile-navigation" aria-label="Mobile navigation" className="fixed inset-y-0 left-0 z-50 flex w-[224px] flex-col border-r border-black/[0.06] bg-sidebar shadow-overlay sm:hidden">
        <div className="flex items-center justify-between px-4 pb-3 pt-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-b from-[#3a9bff] to-primary shadow-glow"><Ghost className="h-[16px] w-[16px] text-white" strokeWidth={2.2} aria-hidden /></div>
            <div className="leading-none"><div className="text-[13px] font-semibold tracking-tight">GhostFit</div><div className="mt-[3px] text-[9px] font-semibold uppercase tracking-[0.12em] text-subtle">Console</div></div>
          </div>
          <button type="button" aria-label="Close navigation" onClick={onClose} className="rounded-[6px] p-1.5 text-subtle hover:bg-black/[0.05] hover:text-text"><X className="h-4 w-4" aria-hidden /></button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2.5 pb-3">{NAV.map((g) => <NavGroup key={g.label} group={g} />)}</nav>
        <div className="border-t border-black/[0.06] px-3 py-2.5 text-[11px] text-muted" title={user?.email}>{user?.email || '—'}</div>
      </aside>
    </>
  );
}
